// Ported from dev-scrips-pm-react settings/services/use-staff-by-id.ts +
// use-update-staff.ts (PROD-1206 nurse self-edit). Mirrors the PA
// use-doctor.ts service-hook pattern (query keys, api-client import path,
// invalidate-on-save).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, put } from '@/shared/api/api-client';
import type { Staff } from '@/types/settings-api';

/**
 * Staff record (the nurse/staff self-edit Account record) — PROD-1206.
 *
 * GET /api/Staff/StaffSetupDetails/{staffSetupId} — full editable record.
 *
 * Flutter parity: `fetchStaffById` in `settings_data_source_impl.dart`.
 * The list endpoint (`/api/Staff/StaffList`) returns a leaner shape; the
 * edit form needs the full record (mobileNumber, specialities,
 * assignStaffToPractice) which only this endpoint carries.
 */
export function useStaffById(staffSetupId: string | null | undefined) {
  return useQuery({
    queryKey: ['settings', 'staff-by-id', staffSetupId],
    queryFn: ({ signal }) => {
      if (!staffSetupId) {
        throw new Error('useStaffById called without staffSetupId');
      }
      return get<Staff>(`/api/Staff/StaffSetupDetails/${staffSetupId}`, {
        signal,
      });
    },
    enabled: !!staffSetupId,
  });
}

/**
 * Update a staff member — PROD-1206.
 *   PUT /api/Staff/{staffSetupId}
 *
 * Flutter parity: `updateStaffById` in `settings_data_source_impl.dart`.
 * The `staffSetupId` appears both in the URL path AND in the body (Flutter
 * sends both); don't second-guess and strip it from the body.
 *
 * Invalidates only the `staff-by-id` GET on success (mirrors the PA
 * `useUpdateDoctor` convention). The PM source also invalidated a
 * `staff-list` key, but PA has no staff-list query in the self-edit
 * surface, so that invalidation is intentionally dropped.
 */
export function useUpdateStaff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (staff: Staff) => {
      if (!staff.staffSetupId) {
        throw new Error('useUpdateStaff requires a non-null staffSetupId');
      }
      return put<unknown>(`/api/Staff/${staff.staffSetupId}`, staff);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'staff-by-id', vars.staffSetupId],
      });
    },
  });
}
